import { useState, useCallback } from 'react';
import { db } from '../../data/base';
import { collection, query, where, getDocs, addDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

/**
 * Servizio per configurare il 3D viewer per prodotti ESISTENTI del catalogo
 * Versione React 18 - configura 3DViewer per prodotti già creati da addProductToCatalog
 * 
 * @param {string} catalogId - ID del catalogo
 * @param {string} catalogProductId - ID del prodotto ESISTENTE in CataloguesProducts
 * @param {string|Array} catalogVariantIds - IDs delle varianti ESISTENTI in CataloguesVariants
 * 
 * @returns {Promise<Object>} Risultato dell'operazione con success e eventuali errori
 * 
 * IMPORTANTE: Questa funzione NON crea prodotti o varianti, li usa ESISTENTI!
 * 
 * LOGICA DI FUNZIONAMENTO:
 * 1. Usa i dati dei prodotti/varianti GIÀ CREATI da addProductToCatalog
 * 2. Controlla se esiste già un 3DViewer per questo catalogId
 * 3. Se esiste: aggiunge prodotto/varianti alla subcollection Products
 * 4. Se non esiste: crea nuovo 3DViewer con campo catalog + subcollection Products
 * 5. Aggiorna gli URL 3D nei documenti CataloguesProducts e CataloguesVariants ESISTENTI
 * 
 * STRUTTURA SUBCOLLECTION PRODUCTS (riferimenti a documenti esistenti):
 * {
 *   id: catalogProductId,           // Riferimento al documento CataloguesProducts esistente
 *   initialModel: firstVariantId,   // Prima variante dalle CataloguesVariants esistenti
 *   list_variants: catalogVariantIds // Riferimenti alle CataloguesVariants esistenti
 * }
 */
export const configure3DViewer = async (catalogId, catalogProductId, catalogVariantIds) => {
    try {
        // Parameter validation
        if (!catalogId || !catalogProductId || !catalogVariantIds) {
            throw new Error('Missing parameters. Required: catalogId, catalogProductId, catalogVariantIds');
        }

        // Normalise catalogVariantIds to array
        const variantIdsArray = Array.isArray(catalogVariantIds) ? catalogVariantIds : [catalogVariantIds];

        if (variantIdsArray.length === 0) {
            throw new Error('At least one catalogVariantId is required');
        }

        // 1. Check if a 3DViewer already exists for this catalog
        const viewersQuery = query(
            collection(db, '3DViewers'),
            where('catalog', '==', catalogId)
        );
        const viewersSnapshot = await getDocs(viewersQuery);

        let viewerRef;
        const firstVariantId = variantIdsArray[0]; // Use first variant as initialModel

        if (!viewersSnapshot.empty) {
            // 1a. The 3DViewer already exists for this catalog - just use it
            const existingViewerDoc = viewersSnapshot.docs[0];
            viewerRef = existingViewerDoc.ref;

            // Check if this specific product already exists in the subcollection
            const productsRef = doc(collection(viewerRef, 'Products'), catalogProductId);
            const productSnap = await getDoc(productsRef);

            if (productSnap.exists()) {
                // Product already exists, merge variants to avoid duplicates
                const productData = productSnap.data();
                const existingVariants = productData.list_variants || [];

                // Create a Set to avoid duplicates and merge arrays
                const allVariants = [...new Set([...existingVariants, ...variantIdsArray])];

                await updateDoc(productsRef, {
                    list_variants: allVariants
                });
            } else {
                // Product doesn't exist in subcollection, create new entry
                await setDoc(productsRef, {
                    id: catalogProductId,
                    initialModel: firstVariantId,
                    list_variants: variantIdsArray
                });
            }
        } else {
            // 2. The 3DViewer doesn't exist, create a new one for this catalog
            viewerRef = await addDoc(collection(db, '3DViewers'), {
                autoRotate: false,
                backgroundColor: "#ffffff",
                backgroundImage: "",
                cameraControls: true,
                catalog: catalogId,
                defaultSpeed: "10",
                disableZoom: false,
                hdr: 5,
                isBackgroundImage: false,
                isFirstModelLoaded: true,
                lightExposition: 1,
                lock: false,
                menuOrientation: 1,
                menuPosition: "bottom",
                menuType: "plane",
                modalQr: false,
                modelList: 10,
                password: "",
                shadowIntensity: 0.5,
                shadowSmooth: 0.3,
                status: "draft"
            });

            // Create the Products subcollection entry (NOT the actual product)
            const productsRef = doc(collection(viewerRef, 'Products'), catalogProductId);
            await setDoc(productsRef, {
                id: catalogProductId,           // References the existing CataloguesProducts document
                initialModel: firstVariantId,   // First variant from the existing CataloguesVariants
                list_variants: variantIdsArray  // All variants from the existing CataloguesVariants
            });
        }

        // 4. Update 3D URLs in CataloguesProducts and CataloguesVariants documents

        // Update CataloguesProducts (once for the product)
        const catalogProductRef = doc(db, 'CataloguesProducts', catalogProductId);
        const newProductUrl = `https://studio.arshades.it/Arshades3ds/${viewerRef.id}/glasses/${catalogProductId}`;
        await updateDoc(catalogProductRef, {
            multiple3dViewerLink: newProductUrl
        });

        // Update CataloguesVariants for each variant
        const variantUrls = [];
        const updatePromises = variantIdsArray.map(async (variantId) => {
            const catalogVariantRef = doc(db, 'CataloguesVariants', variantId);
            const newVariantUrl = `https://studio.arshades.it/Arshades3d/${viewerRef.id}/glasses/${catalogProductId}/variant/${variantId}`;

            await updateDoc(catalogVariantRef, {
                modelViewerUrl: newVariantUrl
            });

            variantUrls.push({
                variantId,
                url: newVariantUrl
            });
        });

        // Wait for all variant updates to complete
        await Promise.all(updatePromises);

        return {
            success: true,
            message: `3D viewer configuration completed successfully for ${variantIdsArray.length} variant(s)`,
            data: {
                catalogId,
                catalogProductId,
                catalogVariantIds: variantIdsArray,
                productUrl: newProductUrl,
                variantUrls: variantUrls,
                viewerId: viewerRef.id,
                totalVariants: variantIdsArray.length
            }
        };

    } catch (error) {
        console.error('Error in 3D viewer configuration:', error);

        return {
            success: false,
            error: error.message,
            code: error.code || 'UNKNOWN_ERROR'
        };
    }
};

/**
 * Hook personalizzato per utilizzare la configurazione del 3D viewer in React 18
 * 
 * @returns {Object} Oggetto con funzione configure e stato di loading
 */
export const useConfigure3DViewer = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const configure = useCallback(async (catalogId, catalogProductId, catalogVariantIds) => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await configure3DViewer(catalogId, catalogProductId, catalogVariantIds);

            if (!result.success) {
                setError(result.error);
            }

            return result;
        } catch (err) {
            const errorMessage = err.message || 'Unknown error occurred';
            setError(errorMessage);
            return {
                success: false,
                error: errorMessage,
                code: 'HOOK_ERROR'
            };
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        configure,
        isLoading,
        error,
        clearError: () => setError(null)
    };
};

/**
 * Funzione batch per configurare più prodotti contemporaneamente
 * 
 * @param {Array} configurations - Array di oggetti con catalogId, catalogProductId, catalogVariantIds
 * @returns {Promise<Object>} Risultato dell'operazione batch
 */
export const configure3DViewerBatch = async (configurations) => {
    try {
        if (!Array.isArray(configurations) || configurations.length === 0) {
            throw new Error('Configurations array is required and must not be empty');
        }

        const results = [];
        const errors = [];

        // Process each configuration
        for (const config of configurations) {
            const { catalogId, catalogProductId, catalogVariantIds } = config;

            try {
                const result = await configure3DViewer(catalogId, catalogProductId, catalogVariantIds);
                results.push({
                    ...config,
                    result
                });
            } catch (error) {
                errors.push({
                    ...config,
                    error: error.message
                });
            }
        }

        return {
            success: errors.length === 0,
            message: `Processed ${results.length} configurations successfully, ${errors.length} failed`,
            data: {
                successful: results,
                failed: errors,
                totalProcessed: configurations.length,
                successCount: results.length,
                errorCount: errors.length
            }
        };

    } catch (error) {
        console.error('Error in batch 3D viewer configuration:', error);

        return {
            success: false,
            error: error.message,
            code: error.code || 'BATCH_ERROR'
        };
    }
};

/**
 * Hook per la configurazione batch del 3D viewer
 * 
 * @returns {Object} Oggetto con funzione configureBatch e stati di loading/progress
 */
export const useConfigure3DViewerBatch = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    const configureBatch = useCallback(async (configurations) => {
        setIsLoading(true);
        setError(null);
        setProgress({ current: 0, total: configurations.length });

        try {
            const results = [];
            const errors = [];

            for (let i = 0; i < configurations.length; i++) {
                const config = configurations[i];
                setProgress({ current: i + 1, total: configurations.length });

                try {
                    const result = await configure3DViewer(
                        config.catalogId,
                        config.catalogProductId,
                        config.catalogVariantIds
                    );

                    results.push({
                        ...config,
                        result
                    });
                } catch (error) {
                    errors.push({
                        ...config,
                        error: error.message
                    });
                }
            }

            const finalResult = {
                success: errors.length === 0,
                message: `Processed ${results.length} configurations successfully, ${errors.length} failed`,
                data: {
                    successful: results,
                    failed: errors,
                    totalProcessed: configurations.length,
                    successCount: results.length,
                    errorCount: errors.length
                }
            };

            if (errors.length > 0) {
                setError(`${errors.length} configurations failed`);
            }

            return finalResult;

        } catch (err) {
            const errorMessage = err.message || 'Unknown error occurred in batch processing';
            setError(errorMessage);
            return {
                success: false,
                error: errorMessage,
                code: 'BATCH_HOOK_ERROR'
            };
        } finally {
            setIsLoading(false);
            setProgress({ current: 0, total: 0 });
        }
    }, []);

    return {
        configureBatch,
        isLoading,
        error,
        progress,
        clearError: () => setError(null)
    };
}; 